import { useCallback, useEffect, useMemo, useState } from "react";

import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import { showpublicFieldName } from "../../../utils/API";
import { isYesNoDomain } from "../../../utils/helper";
import { createSymbol, getLayerSymbology } from "../../../utils/symbologies";
import CustomSwitch from "../../CustomSwitch/CustomSwitch";
import { getMapFilterOptions } from "../../Filter/helpers";
import {
  ColorPolygon,
  SymbolLine,
} from "../../SymbologyLegend/SymbologyLegend-styled";
import {
  StyledLoaderWrapper,
  StyledToggleButton,
  StyledToggleButtonWrapper,
  StyledToggleTitle,
  StyledToggleWrapper,
} from "../ManualWidget-styled";

const getMapFilterSymbols = (layer, config) => {
  if (!layer?.layerConfig) return;

  const symbology = getLayerSymbology(layer, config);
  if (!symbology) return;
  const colorMap = symbology.colorMap || {};
  const colors = colorMap?.default || {};
  if (colorMap) {
    // const colorSymbologyValue = feature.attributes[colorMap.fields[0]] === null ? 'NULL' : feature.attributes[colorMap.fields[0]];
    // const color = colors[colorSymbologyValue] ?? colors.defaultColor;
    const symbols = [];
    Object.keys(colors).forEach((key) => {
      const symbol = createSymbol(
        symbology,
        layer,
        symbology.defaultIcon,
        colors[key],
        symbology
      );
      symbols.push({
        value: key,
        symbol,
      });
    });
    return symbols;
  }
  return [];
};

const FilterToggle = ({
  field,
  options,
  selectedValue,
  onFilterChange,
  t,
  config,
  view,
  layer,
  showSymbol,
}) => {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let handler, timer;
    if (!loading) return;
    view
      .whenLayerView(layer)
      .then((lv) => {
        handler = reactiveUtils.watch(
          () => lv.dataUpdating,
          (isUpdating) => {
            if (!isUpdating) {
              setLoading(false);
            }
          }
        );
      })
      .catch((err) => {
        console.log(err);
      });
    return () => {
      handler?.remove();
    };
  }, [loading]);

  const handleSelection = useCallback(
    (value) => {
      setLoading(true);
      if (
        (Array.isArray(value) && value.includes(selectedValue)) ||
        selectedValue === value
      ) {
        onFilterChange(field.name, []);
      } else {
        onFilterChange(field.name, Array.isArray(value) ? value : [value]);
      }
    },
    [onFilterChange, selectedValue]
  );

  const toggleOptions = useMemo(
    () => getMapFilterOptions({ options, field, t }),
    [field, options, t]
  );
  const symbolOptions = useMemo(() => {
    if (showSymbol) {
      return getMapFilterSymbols(layer, config);
    }

    return [];
  }, [config, layer, showSymbol]);

  const isSwitchableDomain = useMemo(() => {
    return field.domain && isYesNoDomain(field, layer);
  }, [field, layer]);

  const renderOptions = () => {
    if (isSwitchableDomain) {
      return (
        <StyledToggleButton
          style={{
            width: 112,
            justifyContent: "center",
            display: "flex",
          }}
        >
          <CustomSwitch
            labelA={t("layer.domain." + field.name + ".2")}
            labelB={t("layer.domain." + field.name + ".1")}
            checked={selectedValue === 1}
            onChange={() => {
              if (selectedValue !== 1) {
                handleSelection(1);
              } else {
                if (field.name === showpublicFieldName) {
                  handleSelection(1);
                } else {
                  handleSelection([null, 0, 2]);
                }
              }
            }}
          />
        </StyledToggleButton>
      );
    } else {
      return toggleOptions.map((option) => (
        <StyledToggleButton
          key={option.value}
          onClick={() => handleSelection(option.value)}
          selected={selectedValue === option.value}
          opsColor="var(--opsColor)"
        >
          <span>{option.label}</span>
        </StyledToggleButton>
      ));
    }
  };

  const title = useMemo(() => {
    if (showpublicFieldName === field.name) {
      return t("layer.domain.showpublic.filter.1");
    } else {
      return t("layer.fieldAlias." + field.name + ".title", field.alias);
    }
  }, [t]);

  return (
    <StyledToggleWrapper>
      <StyledToggleTitle>
        <p>{title}</p>
      </StyledToggleTitle>
      <StyledToggleButtonWrapper>
        {loading && (
          <StyledLoaderWrapper opsColor="">
            <calcite-loader color={config.opsColor} scale="s"></calcite-loader>
          </StyledLoaderWrapper>
        )}

        {showSymbol
          ? symbolOptions.map((option) => (
              <StyledToggleButton
                key={option.value}
                onClick={() => handleSelection(option.value)}
                selected={selectedValue === option.value}
                opsColor="var(--opsColor)"
              >
                <SymbolLine key={"syml"}>
                  <ColorPolygon color={option.symbol.color} />
                </SymbolLine>
              </StyledToggleButton>
            ))
          : renderOptions()}
      </StyledToggleButtonWrapper>
    </StyledToggleWrapper>
  );
};

export default FilterToggle;
